import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, Send, Users, Image as ImageIcon, X as XIcon,
  CheckCircle2, Mail, Filter, Loader2, History
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * 관리자 → 회원 직접 메시지 발송 페이지
 * 모드: 개별/다중 선택 | 그룹 필터 | 발송 히스토리
 */
export default function AdminMessagesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();

  const [mode, setMode] = useState("select"); // select | filter | history
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  // URL의 prefill (회원 목록에서 진입할 때)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uid = params.get("user_id");
    if (uid) setSelectedIds([uid]);
  }, [location.search]);

  // 필터 모드 상태
  const [filter, setFilter] = useState({
    membership_type: "",
    gender: "",
    country: "",
    role: "",
    age_min: "",
    age_max: "",
  });
  const [filterCount, setFilterCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);

  // 메시지 작성
  const [content, setContent] = useState("");
  const [images, setImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [sending, setSending] = useState(false);

  // 히스토리
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Auth 가드
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || user?.role !== "admin") {
      navigate("/");
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // 회원 목록 로딩
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoadingUsers(true);
        const res = await fetch(`${API_URL}/api/admin/users?limit=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("fetch failed");
        const d = await res.json();
        const list = (d.users || d || []).map((u) => ({
          id: u.id,
          name: u.name || u.username || "이름 미입력",
          username: u.username,
          email: u.email,
          membership_type: u.membership_type || "basic",
          gender: u.gender || "",
          birth_year: u.birth_year || 0,
          country: u.country || "",
          role: u.role || "user",
        }));
        setAllUsers(list);
      } catch (e) {
        console.error(e);
        toast.error("회원 목록 불러오기 실패");
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, [token]);

  // 필터 카운트 미리보기
  useEffect(() => {
    if (mode !== "filter" || !token) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        setCountLoading(true);
        const params = new URLSearchParams();
        Object.entries(filter).forEach(([k, v]) => {
          if (v !== "" && v !== null && v !== undefined) params.set(k, v);
        });
        const res = await fetch(`${API_URL}/api/admin/messages/recipient-count?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ctrl.signal,
        });
        if (res.ok) {
          const d = await res.json();
          setFilterCount(d.count ?? 0);
        }
      } catch {/* aborted */} finally {
        setCountLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [filter, mode, token]);

  // 히스토리 로딩
  useEffect(() => {
    if (mode !== "history" || !token) return;
    (async () => {
      try {
        setLoadingHistory(true);
        const res = await fetch(`${API_URL}/api/admin/messages/history?limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const d = await res.json();
          setHistory(d.messages || []);
        }
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, [mode, token]);

  // 회원 필터링 (검색)
  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.username || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  }, [allUsers, searchQuery]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const selectAllFiltered = () => {
    const ids = filteredUsers.map((u) => u.id);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
  };
  const clearSelection = () => setSelectedIds([]);

  // 이미지 업로드
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("이미지는 10MB 이하만 가능합니다");
      return;
    }
    try {
      setUploadingImage(true);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("file_type", "image");
      const res = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "업로드 실패");
      setImages((prev) => [...prev, d.url]);
      toast.success("이미지 추가됨");
    } catch (err) {
      toast.error(err.message || "업로드 실패");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  // 메시지 발송
  const handleSend = async () => {
    if (!content.trim()) {
      toast.error("메시지 내용을 입력하세요");
      return;
    }
    let payload = { content: content.trim(), images, send_notification: true };
    if (mode === "select") {
      if (selectedIds.length === 0) {
        toast.error("수신자를 선택하세요");
        return;
      }
      payload.user_ids = selectedIds;
    } else if (mode === "filter") {
      const cleaned = {};
      Object.entries(filter).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) {
          cleaned[k] = (k === "age_min" || k === "age_max") ? parseInt(v) : v;
        }
      });
      if (Object.keys(cleaned).length === 0) {
        toast.error("그룹 필터를 1개 이상 설정하세요 (전체 발송은 안전상 비활성화)");
        return;
      }
      if (!filterCount || filterCount === 0) {
        toast.error("필터 조건에 해당하는 수신자가 없습니다");
        return;
      }
      if (!window.confirm(`정말 ${filterCount}명에게 일괄 발송하시겠습니까?`)) return;
      payload.filter = cleaned;
    }

    try {
      setSending(true);
      const res = await fetch(`${API_URL}/api/admin/messages/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "발송 실패");
      toast.success(d.message || `${d.sent_count}명에게 발송 완료`);
      setContent("");
      setImages([]);
      setSelectedIds([]);
    } catch (e) {
      toast.error(e.message || "발송 실패");
    } finally {
      setSending(false);
    }
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-burgundy-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20 pb-12" data-testid="admin-messages-page">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/admin")} className="p-2 rounded-lg hover:bg-stone-100">
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">관리자 메시지 발송</h1>
            <p className="text-sm text-stone-500">회원에게 직접 메시지 + 알림을 전송합니다</p>
          </div>
        </div>

        {/* Mode Tabs */}
        <div className="inline-flex bg-white border border-stone-200 rounded-xl p-1 mb-6 shadow-sm">
          {[
            { id: "select", label: "회원 선택", icon: Users },
            { id: "filter", label: "그룹 필터", icon: Filter },
            { id: "history", label: "발송 내역", icon: History },
          ].map((t) => {
            const Icon = t.icon;
            const active = mode === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setMode(t.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
                  active ? "bg-burgundy-600 text-white shadow" : "text-stone-600 hover:bg-stone-50"
                }`}
                data-testid={`admin-msg-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: 수신자 선택 / 필터 / 히스토리 */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-stone-200 p-5">
            {mode === "select" && (
              <>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="이름, 닉네임, 이메일 검색"
                    className="flex-1 min-w-[200px] px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-300"
                    data-testid="admin-msg-search"
                  />
                  <div className="flex items-center gap-2">
                    <button onClick={selectAllFiltered} className="text-xs px-3 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg" data-testid="select-all-btn">
                      검색결과 전체 선택
                    </button>
                    <button onClick={clearSelection} className="text-xs px-3 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg" data-testid="clear-selection-btn">
                      선택 해제
                    </button>
                  </div>
                </div>
                <p className="text-xs text-stone-500 mb-2">
                  선택됨 <span className="font-bold text-burgundy-600" data-testid="selected-count">{selectedIds.length}</span>명
                  · 표시 {filteredUsers.length}명 / 전체 {allUsers.length}명
                </p>

                <div className="max-h-[520px] overflow-y-auto divide-y divide-stone-100 border border-stone-100 rounded-lg">
                  {loadingUsers ? (
                    <div className="py-10 text-center text-stone-400">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="py-10 text-center text-stone-400 text-sm">검색 결과가 없습니다</div>
                  ) : (
                    filteredUsers.map((u) => {
                      const checked = selectedIds.includes(u.id);
                      return (
                        <label
                          key={u.id}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-stone-50 ${checked ? "bg-burgundy-50/50" : ""}`}
                          data-testid={`user-row-${u.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelect(u.id)}
                            className="w-4 h-4 accent-burgundy-600"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-stone-800 truncate">
                              {u.name}
                              {u.username && <span className="text-stone-400 text-xs ml-1.5">@{u.username}</span>}
                            </p>
                            <p className="text-xs text-stone-500 truncate">
                              {u.email} · {u.membership_type === "vip" ? "VIP" : u.membership_type === "premium" ? "프리미엄" : u.membership_type === "standard" ? "정회원" : "일반"}
                            </p>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </>
            )}

            {mode === "filter" && (
              <div className="space-y-4">
                <p className="text-sm text-stone-600">
                  조건을 설정하면 매칭되는 회원 전체에게 일괄 발송됩니다.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-1">멤버십</label>
                    <select
                      value={filter.membership_type}
                      onChange={(e) => setFilter({ ...filter, membership_type: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
                      data-testid="filter-membership"
                    >
                      <option value="">전체</option>
                      <option value="basic">일반</option>
                      <option value="standard">정회원</option>
                      <option value="premium">프리미엄</option>
                      <option value="vip">VIP</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-1">성별</label>
                    <select
                      value={filter.gender}
                      onChange={(e) => setFilter({ ...filter, gender: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
                      data-testid="filter-gender"
                    >
                      <option value="">전체</option>
                      <option value="male">남성</option>
                      <option value="female">여성</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-1">국가</label>
                    <select
                      value={filter.country}
                      onChange={(e) => setFilter({ ...filter, country: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
                      data-testid="filter-country"
                    >
                      <option value="">전체</option>
                      <option value="한국">한국</option>
                      <option value="미국">미국</option>
                      <option value="캐나다">캐나다</option>
                      <option value="호주">호주</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-1">역할</label>
                    <select
                      value={filter.role}
                      onChange={(e) => setFilter({ ...filter, role: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
                      data-testid="filter-role"
                    >
                      <option value="">전체</option>
                      <option value="user">일반 회원</option>
                      <option value="admin">관리자</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-1">최소 나이</label>
                    <input
                      type="number"
                      value={filter.age_min}
                      onChange={(e) => setFilter({ ...filter, age_min: e.target.value })}
                      placeholder="예: 40"
                      className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
                      data-testid="filter-age-min"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-1">최대 나이</label>
                    <input
                      type="number"
                      value={filter.age_max}
                      onChange={(e) => setFilter({ ...filter, age_max: e.target.value })}
                      placeholder="예: 59"
                      className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
                      data-testid="filter-age-max"
                    />
                  </div>
                </div>
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <Users className="w-4 h-4 text-amber-600" />
                  <p className="text-sm text-amber-800">
                    매칭 수신자:{" "}
                    {countLoading ? (
                      <Loader2 className="w-3 h-3 inline animate-spin" />
                    ) : (
                      <span className="font-bold" data-testid="filter-recipient-count">{filterCount ?? 0}명</span>
                    )}
                  </p>
                </div>
              </div>
            )}

            {mode === "history" && (
              <div>
                <p className="text-sm text-stone-600 mb-3">최근 발송한 메시지 50건</p>
                {loadingHistory ? (
                  <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-burgundy-500" /></div>
                ) : history.length === 0 ? (
                  <div className="py-10 text-center text-stone-400 text-sm">발송 내역이 없습니다</div>
                ) : (
                  <div className="space-y-2 max-h-[520px] overflow-y-auto">
                    {history.map((m) => (
                      <div key={m.id} className="p-3 border border-stone-200 rounded-lg hover:bg-stone-50">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-stone-800">→ {m.receiver_name}</p>
                          <span className="text-xs text-stone-400">{new Date(m.created_at).toLocaleString("ko-KR")}</span>
                        </div>
                        <p className="text-sm text-stone-600 line-clamp-2 whitespace-pre-wrap">{m.content}</p>
                        {m.images && m.images.length > 0 && (
                          <p className="text-xs text-stone-400 mt-1">📎 이미지 {m.images.length}장</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: 작성 패널 */}
          {mode !== "history" && (
            <div className="bg-white rounded-2xl border border-stone-200 p-5 h-fit sticky top-24">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-5 h-5 text-burgundy-600" />
                <h2 className="font-bold text-stone-800">메시지 작성</h2>
              </div>

              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                placeholder="회원에게 보낼 메시지 내용을 작성해주세요"
                className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-300 resize-none"
                data-testid="admin-msg-content"
              />

              {/* 이미지 첨부 */}
              <div className="mt-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {images.map((url, idx) => (
                    <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border border-stone-200">
                      <img
                        src={url.startsWith("http") ? url : `${API_URL}${url}`}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setImages((prev) => prev.filter((_, i) => i !== idx))}
                        className="absolute top-0 right-0 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center"
                      >
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <label className={`w-16 h-16 rounded-lg border-2 border-dashed border-stone-300 flex flex-col items-center justify-center cursor-pointer hover:bg-stone-50 ${uploadingImage ? "opacity-50" : ""}`}>
                    {uploadingImage ? (
                      <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                    ) : (
                      <>
                        <ImageIcon className="w-4 h-4 text-stone-400" />
                        <span className="text-[10px] text-stone-400 mt-0.5">추가</span>
                      </>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploadingImage} data-testid="admin-msg-image-input" />
                  </label>
                </div>
                <p className="text-[11px] text-stone-400 mt-2">JPG/PNG/WEBP · 최대 10MB</p>
              </div>

              {/* 발송 정보 요약 */}
              <div className="mt-4 p-3 bg-stone-50 rounded-lg text-sm text-stone-600">
                {mode === "select" ? (
                  <p>
                    수신자:{" "}
                    <span className="font-bold text-burgundy-700">
                      {selectedIds.length}명
                    </span>
                  </p>
                ) : (
                  <p>
                    그룹 필터 매칭:{" "}
                    <span className="font-bold text-burgundy-700">{filterCount ?? 0}명</span>
                  </p>
                )}
                <p className="text-xs text-stone-400 mt-1">받는 사람의 메시지함과 알림 벨에 동시 표시됩니다</p>
              </div>

              <Button
                onClick={handleSend}
                disabled={sending || !content.trim() || (mode === "select" && selectedIds.length === 0)}
                className="w-full mt-4 bg-burgundy-600 hover:bg-burgundy-700 text-white disabled:opacity-50"
                data-testid="admin-msg-send-btn"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> 발송 중...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> 발송하기</>
                )}
              </Button>

              {!sending && content.trim() && (
                <p className="text-[11px] text-emerald-600 mt-2 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> 발송 준비 완료
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
